import React, { Component } from 'react';
import { Message } from '@messageformat/react';
import { Alert } from '@op/opds.opux';

interface ErrorBoundaryState {
    hasError: boolean;
    errorId?: string | unknown;
}

interface ErrorBoundaryProps {
    children: React.ReactNode;
}

const formatLogMessage = (message: string) =>
    message.replace(/( ){2}/g, '').replace(/\n/g, ', ');

/**
 * Boundary for catching errors
 * - Renders error message when error is caught
 * - Sends error stack trace and formatted component stack to server side
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    displayName = 'ErrorBoundary';

    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, errorId: undefined };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        const { message, stack } = error || {};
        const { componentStack } = errorInfo || {};

        this.setState((state) => ({
            ...state,
            errorId: error.cause,
        }));

        const logMessage = formatLogMessage(
            `Message: (${message}). Stack: (${stack}). ComponentStack: (${componentStack}).`,
        );

        // eslint-disable-next-line no-console
        console.log(logMessage);
    }

    render() {
        if (this.state.hasError) {
            return (
                <Alert type='critical' contentType='static'>
                    <Message
                        id={
                            this.state.errorId
                                ? `Error_Notification_${this.state.errorId}`
                                : `Error_Generic`
                        }
                    />
                </Alert>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
